export function useSignatureRequest(id: MaybeRefOrGetter<string>) {
  const { isDirectSign } = storeToRefs(useDirectSignStore())
  const { srRepository, directSrRepository } = useApi()

  const key = computed(() => `sr:${toValue(id)}`)

  return useAsyncData(
    key,
    async () => {
      const requestId = toValue(id)

      return isDirectSign.value ? directSrRepository.get(requestId) : srRepository.get(requestId)
    },
    {
      lazy: true,
      dedupe: 'defer',
    }
  )
}

export function useSignatureRequestEvents(id: MaybeRefOrGetter<string>) {
  const { isDirectSign } = storeToRefs(useDirectSignStore())
  const { srRepository, directSrRepository } = useApi()

  const key = computed(() => `events:${toValue(id)}`)

  return useAsyncData(
    key,
    async () => {
      const requestId = toValue(id)

      return isDirectSign.value ? directSrRepository.getEvents(requestId) : srRepository.getEvents(requestId)
    },
    {
      lazy: true,
      dedupe: 'defer',
    }
  )
}

export function useSignatureRequests(params: MaybeRefOrGetter<SignatureRequestPaginationRequest>) {
  const { srRepository } = useApi()

  const key = computed(() => `srs:${JSON.stringify(toValue(params))}`)

  return useAsyncData(
    key,
    async () => {
      return srRepository.getItems(toValue(params))
    },
    {
      lazy: true,
      dedupe: 'defer',
    }
  )
}

export interface SignatureItem extends SignatureData {
  firstRow: string
  secondRow?: string
  displayStatus: string
  isRemovable: boolean
}

export function useSignatureRequestHelpers() {
  const { t } = useI18n()

  const userStore = useUserStore()
  const { email, signatureQualities, signerIdentityEmail, groupVersions } = storeToRefs(userStore)
  const directSignStore = useDirectSignStore()
  const { isDirectSign, username } = storeToRefs(directSignStore)

  const { activePricePlanId } = useUserPlanInfo()

  const isOpenForInvitation = (signatureRequest: SignatureRequestData): boolean => {
    return signatureRequest.signatures.length === 0 && signatureRequest.statusOverall.toLowerCase() === 'open'
  }

  const isTerminated = (signatureRequest: SignatureRequestData): boolean => {
    return signatureRequest.statusOverall.toLowerCase() !== 'open'
  }

  const isOpen = (signatureRequest: SignatureRequestData): boolean => {
    return signatureRequest.statusOverall.toLowerCase() === 'open'
  }

  const isDraft = (signatureRequest: SignatureRequestData): boolean => {
    return signatureRequest.statusOverall.toLowerCase() === 'draft'
  }

  // TODO: This includes "legacy" drafts, should be harmonised with `isDraft`
  const isEditable = (signatureRequest: SignatureRequestData) => {
    return (
      isDraft(signatureRequest) || (signatureRequest.statusOverall === 'OPEN' && !signatureRequest.signatures.length)
    )
  }

  const isSigned = (signatureRequest: SignatureRequestData): boolean => {
    return signatureRequest.statusOverall.toLowerCase() === 'signed'
  }

  const isExpired = (signatureRequest: SignatureRequestData): boolean => {
    return signatureRequest.statusOverall.toLowerCase() === 'expired'
  }

  const hasExpiration = (signatureRequest: SignatureRequestData): boolean => {
    return (
      !!signatureRequest.expiresAt &&
      (isOpen(signatureRequest) || isDraft(signatureRequest) || isExpired(signatureRequest))
    )
  }

  const hasUserSigned = (signatureRequest: SignatureRequestData): boolean => {
    const signatures =
      signatureRequest.signatures?.filter(signature => signature.accountEmail === userStore.email) || []
    return signatures.length > 0 && signatures.every(signature => signature.statusCode.toLowerCase() === 'signed')
  }

  const isMemberOfSignerGroup = (signature: SignatureData | PositionedSignature): boolean => {
    return !!signature.signerGroup?.versionId && groupVersions.value?.has(signature.signerGroup.versionId)
  }

  const getCurrentUserObserver = (signatureRequest: SignatureRequestData): SignatureRequestObserver | undefined => {
    return signatureRequest.observers?.find(observer => observer.userInfo?.email === userStore.email)
  }

  const isToSign = (signatureRequest: SignatureRequestData) => {
    const signatures = signatureRequest.signatures ?? []
    return signatures.some(signature => {
      return (
        (signature.accountEmail === userStore.email || isMemberOfSignerGroup(signature)) &&
        signature.statusCode === 'OPEN'
      )
    })
  }

  const isToSignAndOpen = (signatureRequest: SignatureRequestData) => {
    return isToSign(signatureRequest) && isOpen(signatureRequest)
  }

  const isOwner = (signatureRequest: SignatureRequestData): boolean => {
    return signatureRequest.owner === userStore.email
  }

  const getUpdateUrl = (signatureRequest: SignatureRequestData): string => {
    let url = `/profile/signature-standards/update?sr=${signatureRequest.id}`
    url += `&q=${signatureRequest.quality.substring(0, 3).toLowerCase()}`
    if (signatureRequest.legislation) url += `&l=${signatureRequest.legislation.toLowerCase()}`
    return url
  }

  // TODO: This is very unwieldy, and we should coordinate with the backend to make this more consistent
  const mapSignatureRequestStatus = (signatureRequest: SignatureRequest): string => {
    const status = signatureRequest.statusOverall
    switch (status) {
      case 'OPEN':
        if (isToSignAndOpen(signatureRequest)) {
          return 'to_sign'
        } else {
          return 'waiting'
        }
      case 'DRAFT':
        return 'draft'
      case 'DECLINED':
        return 'cancelled'
      case 'SIGNED':
        return 'completed'
      case 'WITHDRAWN':
        return 'cancelled'
      case 'EXPIRED':
        return 'cancelled'
      default:
        return ''
    }
  }

  // TODO: This is very unwieldy, and we should coordinate with the backend to make this more consistent
  const mapSignatureStatus = (status: string): string => {
    switch (status) {
      case 'OPEN':
        return 'pending'
      case 'WAITING':
        return 'queued'
      case 'DECLINED':
        return 'declined'
      case 'SIGNED':
        return 'signed'
      case 'WITHDRAWN':
        return 'withdrawn'
      case 'EXPIRED':
        return 'cancelled'
      default:
        return ''
    }
  }

  const getDisplayStatus = (signatureRequest: SignatureRequestData): string => {
    return mapSignatureRequestStatus(signatureRequest)
  }

  const getCurrentUserPermissions = (signatureRequest: SignatureRequestData) => {
    const permissions = new Set<SignatureRequestPermission>()

    const userIdentity = email.value

    const currentSignature = getCurrentUserCurrentSignature(signatureRequest)

    // Everyone with access can download documents
    permissions.add('DOWNLOAD')

    // Observers can do actions regardless of sr status (example: self-remove)
    if (getCurrentUserObserver(signatureRequest)) {
      permissions.add('OBSERVE').add('SELF_REMOVE_OBSERVER')
    }

    if (isOpenForInvitation(signatureRequest)) {
      if (!userStore.isFreeUser) {
        permissions.add('INVITE').add('REMOVE')
      }
      return permissions
    } else if (isSigned(signatureRequest) && !isDirectSign.value) {
      permissions.add('DOWNLOAD_PROTOCOL').add('REMOVE')
      return permissions
    } else if (isDraft(signatureRequest)) {
      permissions.add('CONTINUE_SR').add('DOWNLOAD_PROTOCOL').add('REMOVE')
      return permissions
    } else if (isTerminated(signatureRequest)) {
      if (!isDirectSign.value) {
        permissions.add('REMOVE')
      }
      return permissions
    }

    if (signatureRequest.readAccess.includes(userIdentity)) permissions.add('READ')
    if (signatureRequest.writeAccess.includes(userIdentity)) {
      permissions.add('WRITE')
      permissions.add('ADD_SIGNER')
      permissions.add('ADD_OBSERVER')
      if (!userStore.isFreeUser) permissions.add('REMIND')
    }

    if (activePricePlanId.value && !['sme_v2', 'INDIVIDUAL_V1', 'INDIVIDUAL_V2'].includes(activePricePlanId.value)) {
      permissions.add('DOWNLOAD_PROTOCOL')
    }
    if (isToSign(signatureRequest)) {
      permissions.add('SIGN')
      if (!permissions.has('WRITE')) permissions.add('DECLINE')
      if (
        !permissions.has('OBSERVE') &&
        !isDirectSign.value &&
        currentSignature &&
        !isMemberOfSignerGroup(currentSignature)
      )
        permissions.add('ASSIGN')
    }
    if (permissions.has('WRITE') && !permissions.has('OBSERVE') && !isDraft(signatureRequest))
      permissions.add('WITHDRAW')
    if (isDraft(signatureRequest)) {
      if (!isDirectSign.value) permissions.add('INVITE')
      if (!permissions.has('OBSERVE')) permissions.add('REMOVE')
      if (permissions.has('WRITE')) permissions.add('CONTINUE_SR')
    }

    return permissions
  }

  const getCurrentSigningDetails = (signatureRequest: SignatureRequestData): SigningDetails => {
    const quality = stringToCamelCase(signatureRequest.quality.toLowerCase())

    // TODO: Wave should not need to fix interface discrepancies
    // SES directSign users won't have their email verified on first visit
    // which will result in bbq sending null for all qualities. wave will fix
    // this by setting ses.any: 'ses' in this case
    if (quality === 'ses' && Boolean(signerIdentityEmail.value)) {
      return { legislation: 'any', quality: 'ses', provider: 'AIS' }
    }

    const legislation = ['demo', 'aes', 'ses', 'part11'].includes(quality)
      ? 'any'
      : (signatureRequest.legislation?.toLowerCase() ?? 'any')

    const details = signatureQualities.value[quality]?.[legislation]

    if (details) {
      return {
        quality: details.quality.toLowerCase(),
        legislation: details.legislation.toLowerCase(),
        provider: details.provider,
      }
    }

    return { quality: null, legislation: null, provider: null }
  }

  const sealMatchesSrLegislation = (
    details: LegislationDetails | null,
    srQuality: string,
    srLegislation: string
  ): boolean => {
    if (srQuality !== 'qes') return true
    // A QES seal with unknown/missing legislation is deliberately not offered
    return details?.legislation?.toLowerCase() === srLegislation
  }

  const getApplicableSeals = (signatureRequest?: SignatureRequestData | null): string[] => {
    if (!signatureRequest) return []

    const srQuality = signatureRequest.quality.toLowerCase()
    const srLegislation = signatureRequest.legislation?.toLowerCase() ?? ''

    const sealsBySrQuality = signatureQualities.value.seal?.[srQuality as 'aes' | 'qes'] ?? {}

    return Object.entries(sealsBySrQuality)
      .filter(([, details]) => sealMatchesSrLegislation(details, srQuality, srLegislation))
      .map(([accountName]) => accountName)
  }

  const getInvitedBy = (item: SignatureRequest): string => {
    // owner/editor
    if (getCurrentUserPermissions(item).has('WRITE')) {
      return item.owner
    }
    // signer
    const signature = getCurrentUserCurrentSignature(item)
    if (signature?.invitedBy) {
      return signature.invitedBy
    }
    // observer
    const userObservers = item.observers.filter(observer => observer.userInfo?.email === email.value)
    if (userObservers.length > 0 && userObservers[0]?.invitedBy) {
      return userObservers[0].invitedBy
    }

    return item.owner
  }

  const getInvitedAt = (item: SignatureRequest): string => {
    const signature = getCurrentUserCurrentSignature(item)
    const invitedAt = signature?.invitedAt ?? item.createdAt
    const { formatDate } = useDate()
    return `→ ${formatDate(new Date(invitedAt), 'dd MMM yyyy')}`
  }

  const getBrandingLogo = (signatureRequest: SignatureRequest): BrandingLogo | null => {
    if (!signatureRequest.branding) return null
    return signatureRequest.branding.logo
  }

  const firstRowSignerInfo = (signature: SignatureData) => {
    // add name if it is known, i.e. user has an account or is a NAS
    if (signature.signerIdentityData && Object.keys(signature.signerIdentityData).length > 0) {
      // Use first_ and last_name if they are known or email if it was provided (SES directSign GUI).
      if (signature.signerIdentityData.firstName || signature.signerIdentityData.lastName) {
        return `${signature.signerIdentityData.firstName ?? ''} ${signature.signerIdentityData.lastName ?? ''}`
      } else if (signature.signerIdentityData.emailAddress) {
        return signature.signerIdentityData.emailAddress
      }
    }
    if (signature.accountEmail) {
      // this user has no account yet, but was invited using their email
      return signature.accountEmail
    }

    if (signature.signerGroup?.name) {
      return signature.signerGroup.name
    }
    return ''
  }

  const secondRowSignerInfo = (signature: SignatureData) => {
    if (signature.signerIdentityData && Object.keys(signature.signerIdentityData).length > 0) {
      // add email if it is known, i.e. user is not a NAS
      if (signature.accountEmail?.includes('@')) {
        // this is a regular user
        return signature.accountEmail
      } else {
        // this is a NAS
        return t('details.signer.no_account_signing')
      }
    }
    if (signature.signerGroup?.id) {
      return
    }
    // this user has no account yet, but was invited using their email
    return t('details.signer.no_account_yet')
  }

  const getSignaturesInfo = (signatureRequest: SignatureRequestData): SignatureItem[] => {
    const isRemovalPermitted = !isTerminated(signatureRequest)

    const pendingSignatures = signatureRequest.signatures.filter(signature =>
      ['OPEN', 'WAITING'].includes(signature.statusCode)
    )

    const hasCompletedSignatures = signatureRequest.signatures.some(signature => signature.statusCode === 'SIGNED')

    return signatureRequest.signatures.map((signature, _, { length: signaturesCount }) => {
      let isRemovable = isRemovalPermitted

      if (signaturesCount === 1) {
        // The only remaining signer cannot be removed
        isRemovable = false
      } else if (hasCompletedSignatures && pendingSignatures.length === 1) {
        // The only pending signature is removable if other signatures are already completed
        isRemovable = signature.sid === pendingSignatures[0].sid
      }

      return {
        ...signature,
        firstRow: firstRowSignerInfo(signature),
        secondRow: secondRowSignerInfo(signature),
        displayStatus: mapSignatureStatus(signature.statusCode),
        isRemovable,
      }
    })
  }

  const getCurrentDelegateEvents = (
    signatureRequest: SignatureRequestData,
    events: SignatureRequestEvent[]
  ): (SignatureRequestEvent & { isMessageDisplayed: boolean })[] => {
    return events
      .filter(event => event.type === 'DELEGATE')
      .map(event => ({
        ...event,
        type: event.to.includes(event.triggeredByEmail) ? 'ADD_SIGNER' : 'DELEGATE',
        isMessageDisplayed:
          Boolean(event.message) &&
          [...signatureRequest.writeAccess, event.triggeredByEmail, ...event.to].includes(email.value),
      }))
  }

  const getCurrentUserCurrentSignature = (signatureRequest: SignatureRequestData): SignatureData | null => {
    const userOwnedSignatures = signatureRequest.signatures.filter(
      signature =>
        signature.accountEmail === email.value ||
        signature.accountEmail === username.value ||
        isMemberOfSignerGroup(signature)
    )
    if (userOwnedSignatures.length === 0) {
      return null
    }

    if (userOwnedSignatures.length === 1) {
      return userOwnedSignatures[0] ?? null
    }

    const openSignaturesSorted = userOwnedSignatures
      .filter(signature => signature.statusCode === 'OPEN')
      .sort((a, b) => a.sequence - b.sequence)

    return openSignaturesSorted[0] ?? null
  }

  const getCurrentUserDocumentSignatures = (signatureRequest: SignatureRequestData): DocumentSignature[] => {
    const currentSignature = getCurrentUserCurrentSignature(signatureRequest)
    if (!currentSignature || !currentSignature.documentSignatures) {
      return []
    }

    return currentSignature.documentSignatures
  }

  const getDocumentsToSign = (signatureRequest: SignatureRequestData): string[] => {
    const documentSignatures = getCurrentUserDocumentSignatures(signatureRequest)
    const documentsToSign = documentSignatures
      .map(docSig => signatureRequest.documents.find(doc => doc.id === docSig.documentId))
      .filter(doc => doc !== undefined)
    return documentsToSign.map(doc => doc.name)
  }

  const getOtherDocuments = (signatureRequest: SignatureRequestData): string[] => {
    const documentSignatures = getCurrentUserDocumentSignatures(signatureRequest)
    const otherDocuments = signatureRequest.documents.filter(
      doc => !documentSignatures.some(docSig => docSig.documentId === doc.id)
    )
    return otherDocuments.map(doc => doc.name)
  }

  return {
    getUpdateUrl,
    getDisplayStatus,
    getCurrentUserCurrentSignature,
    getCurrentUserPermissions,
    getCurrentSigningDetails,
    getApplicableSeals,
    getInvitedBy,
    getInvitedAt,
    isOpenForInvitation,
    isTerminated,
    isOpen,
    isDraft,
    isExpired,
    hasExpiration,
    isMemberOfSignerGroup,
    isEditable,
    isToSign,
    isToSignAndOpen,
    hasUserSigned,
    isOwner,
    getBrandingLogo,
    getSignaturesInfo,
    getCurrentDelegateEvents,
    getCurrentUserDocumentSignatures,
    getDocumentsToSign,
    getOtherDocuments,
    mapSignatureStatus,
    mapSignerDisplayString: firstRowSignerInfo,
    getCurrentUserObserver,
  }
}
